import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { isAdmin, canApprove, isAccountant } from "./lib/roles";
import { NotificationProvider } from "./contexts/NotificationContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ExpensesPage from "./pages/ExpensesPage";
import NewExpensePage from "./pages/NewExpensePage";
import TransactionsPage from "./pages/TransactionsPage";
import ApprovalsPage from "./pages/ApprovalsPage";
import SettingsPage from "./pages/SettingsPage";
import LogsPage from "./pages/LogsPage";
import EulaPage from "./pages/legal/EulaPage";
import PrivacyPage from "./pages/legal/PrivacyPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <div className="animate-pulse text-zinc-400 text-sm tracking-widest uppercase">Loading</div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  // Force-change-on-first-login: when admin set a temp password, the user
  // must pick a new one before they can access anything. The change page
  // itself bypasses this guard (else we'd loop forever).
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!isAdmin(user)) return <Navigate to="/" replace />;
  return children;
}

function ApproverRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  // Accountants are read-only viewers of the approvals queue — they see the
  // same list but the page hides every action button for them.
  if (!canApprove(user) && !isAccountant(user)) return <Navigate to="/" replace />;
  return children;
}

function WriterRoute({ children }) {
  // Pages that involve creating or editing expenses must NEVER be reachable by
  // the read-only accountant role — block direct URL access too.
  const { user, loading } = useAuth();
  if (loading) return null;
  if (isAccountant(user)) return <Navigate to="/expenses" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <div className="animate-pulse text-zinc-400 text-sm tracking-widest uppercase">Loading</div>
    </div>
  );

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />

      {/* Public legal pages — referenced by Intuit App Assessment Questionnaire */}
      <Route path="/eula" element={<EulaPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* Public invite acceptance — no auth required (uses one-time token in URL) */}
      <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
      {/* Public password-reset flow — anti-enumeration, single-use tokens */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      {/* Authenticated change-password — used by force-change-on-first-login */}
      <Route path="/change-password" element={
        user ? <ChangePasswordPage /> : <Navigate to="/login" replace />
      } />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="expenses/new" element={<WriterRoute><NewExpensePage /></WriterRoute>} />
        <Route path="expenses/:id/edit" element={<WriterRoute><NewExpensePage /></WriterRoute>} />
        <Route path="transactions" element={<TransactionsPage />} />
        <Route path="approvals" element={<ApproverRoute><ApprovalsPage /></ApproverRoute>} />
        <Route path="settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
        <Route path="logs" element={<AdminRoute><LogsPage /></AdminRoute>} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <AppRoutes />
        </NotificationProvider>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
